/** Bot behaviour (docs/fight.md §8). All numbers are placeholders except the prototype base. */

export type BotDifficulty = 'easy' | 'normal' | 'hard';

export interface DifficultyConfig {
  /** How old the bot's view of the opponent is. */
  reactionSeconds: number;
  decisionMin: number;
  decisionMax: number;
  /** Chance to block a strike or signature it sees coming. */
  blockChance: number;
  /** Chance to answer a committed move with the move that beats it. */
  readChance: number;
  /** Chance to strike back inside its own punish window after a read (D14). */
  punishChance: number;
}

/** Bot names (never a fighter's name, so the name tag can't say "Moss" on Spike). */
export const BOT_NAMES = ['Pebble', 'Thistle', 'Bramble', 'Flint', 'Clover', 'Nettle', 'Gravel', 'Burdock'] as const;

/** A bot's skill ranks mirror the player's, scaled by difficulty (placeholder rule). */
export const BOT_RANK_SCALE: Record<BotDifficulty, number> = { easy: 0.5, normal: 0.8, hard: 1 };

export const BOT = {
  difficulties: {
    easy: { reactionSeconds: 0.4, decisionMin: 0.35, decisionMax: 0.7, blockChance: 0.15, readChance: 0, punishChance: 0.25 },
    normal: { reactionSeconds: 0.25, decisionMin: 0.2, decisionMax: 0.48, blockChance: 0.33, readChance: 0.25, punishChance: 0.6 },
    hard: { reactionSeconds: 0.18, decisionMin: 0.15, decisionMax: 0.35, blockChance: 0.5, readChance: 0.5, punishChance: 0.9 },
  } satisfies Record<BotDifficulty, DifficultyConfig>,

  /** Prototype behaviour; this is "normal" before plan and difficulty adjustments. */
  base: {
    strafeSwitchChance: 0.25,
    /** Backs off below this: the cost of a grapple. */
    lowStamina: 26,
    /** Approach when farther than this × reach. */
    approachRange: 0.92,
    blockSeconds: 0.4,
    reactiveBlockSeconds: 0.45,
    /** Watch for incoming strikes up to reach + this. */
    reactRangeExtra: 90,
    signatureWhenFull: 0.5,
    strike: 0.3,
    grapple: 0.09,
    block: 0.11,
    /** The opponent is rocked (D13): go for the finish. Chances per decision in range. */
    finish: { signature: 0.8, strike: 0.6, grapple: 0.2, grappleIfBlocking: 0.6 }, // placeholder
    /** While finishing, the next decision comes after decisionMin × this. */
    finishDecisionMul: 0.6, // placeholder
    /** Rocked itself: back off, and cover up when the opponent is within this × reach. */
    rockedCoverRange: 1.4, // placeholder
    /** Rocked: block chance = the difficulty's blockChance × this. */
    rockedBlockMul: 1.8, // placeholder
  },

  /** Game plan effects (docs/game-design.md §8). Aggression is 0–100, 50 = as the base. */
  plan: {
    styles: {
      pressure: { approach: 1.3, strike: 1.3, grapple: 0.9, block: 0.6 },
      counter: { approach: 0.8, strike: 0.9, grapple: 1.0, block: 1.8 },
      grinder: { approach: 1.2, strike: 0.7, grapple: 2.2, block: 0.9 },
    },
    roundPlans: {
      feelOut: { aggression: -30, block: 1.3 },
      push: { aggression: 0, block: 1 },
      finish: { aggression: 30, block: 0.8 },
    },
    whenHurt: {
      /** Health share that counts as hurt. */
      threshold: 0.35,
      coverStaminaTarget: 60,
      swingBackAggression: 40,
    },
  },
} as const;
